/* eslint-disable no-useless-escape */
const baseColorMap = {
  p: {
    textClass: 'text-primary-l',
    borderClass: 'border-primary',
    lineBgClass: 'bg-primary',
    hrBorderClass: 'border-primary',
    markBgClass: 'bg-primary',
    codeBgClass: 'bg-primary',
  },
  s: {
    textClass: 'text-secondary',
    borderClass: 'border-secondary',
    lineBgClass: 'bg-secondary-d',
    hrBorderClass: 'border-secondary',
    markBgClass: 'bg-secondary-d',
    codeBgClass: 'bg-secondary',
  },
};

['red', 'orange', 'yellow', 'lime', 'green', 'blue', 'purple', 'pink'].forEach(
  color => {
    baseColorMap[color] = {
      textClass: `text-${color}`,
      borderClass: `border-${color}`,
      lineBgClass: `bg-${color}`,
      hrBorderClass: `border-${color}`,
      markBgClass: `bg-${color}`,
      codeBgClass: `bg-${color}`,
    };
  }
);

const colorTokens = Object.keys(baseColorMap);

const buildColorRules = (patternFactory, replacementFactory, reverseFactory) =>
  colorTokens.map(token => ({
    pattern: patternFactory(token),
    replacement: replacementFactory(baseColorMap[token]),
    ...(reverseFactory ? reverseFactory(token, baseColorMap[token]) : {}),
  }));

export const mdRules = [
  {
    name: 'uncheck',
    pattern: /\[\] (.*)/g,
    replacement:
      '<input type="checkbox" isChecked="false" data-content="•$1•" />',
    reversePattern:
      /<input type=\"checkbox\" isChecked=\"false\" data-content=Æ(.*)Æ \/>/g,
    reverseReplacement: '[] $1',
  },
  {
    name: 'uncheck',
    pattern: /\[x\] (.*)/g,
    replacement:
      '<input type="checkbox" checked isChecked="true" data-content="•$1•" />',
    reversePattern:
      /<input type=\"checkbox\" checked isChecked=\"true\" data-content=Æ(.*)Æ \/>/g,
    reverseReplacement: '[x] $1',
  },

  ...buildColorRules(
    token => new RegExp(`^> (.*) \\{${token}\\}`, 'gm'),
    color =>
      `<div class="border-l-2 ${color.borderClass} pl-1 mx-1 opacity-70 text-left">$1</div>`
  ),
  {
    name: 'Block quote',
    pattern: /^> (.*)/gm,
    replacement:
      '<div class="border-l-2 border-contrast border-opacity-50 pl-1 mx-1 opacity-70 text-left">$1</div>',
  },

  ...buildColorRules(
    token => new RegExp(`^#{3} (.*) \\{${token}\\}`, 'gm'),
    color =>
      `<div class="text-lg font-bold font-condensed uppercase mt-1 ${color.textClass}">$1</div>`
  ),
  {
    name: 'H3',
    pattern: /^#{3} (.*)/gm,
    replacement:
      '<div class="text-lg font-bold font-condensed uppercase mt-1">$1</div>',
  },

  ...buildColorRules(
    token => new RegExp(`^#{2} (.*) \\{${token}\\}`, 'gm'),
    color =>
      `<div class="flex gap-1 items-center font-condensed uppercase text-xl mt-1 mb-05 ${color.textClass}">$1<div class="flex-1 ${color.lineBgClass} bg-opacity-50" style="height: 1px"></div></div>`
  ),
  {
    name: 'H2',
    pattern: /^#{2} (.*)/gm,
    replacement:
      '<div class="flex gap-1 items-center font-condensed uppercase text-xl mt-1 mb-05">$1<div class="flex-1 bg-contrast bg-opacity-30" style="height: 1px"></div></div>',
  },

  ...buildColorRules(
    token => new RegExp(`^# (.*) \\{${token}\\}`, 'gm'),
    color =>
      `<div class="flex gap-05 items-center font-condensed uppercase text-2xl mt-1 mb-05 ${color.textClass}"><div class="flex-1 ${color.lineBgClass}" style="height: 1px"></div>$1<div class="flex-1 ${color.lineBgClass}" style="height: 1px"></div></div>`
  ),
  {
    name: 'H1',
    pattern: /^# (.*)/gm,
    replacement:
      '<div class="flex gap-05 items-center font-condensed uppercase mt-1 mb-05 text-2xl"><div class="flex-1 bg-contrast bg-opacity-50" style="height: 1px"></div>$1<div class="flex-1 bg-contrast bg-opacity-50" style="height: 1px"></div></div>',
  },

  ...buildColorRules(
    token => new RegExp(`^(---) \\{${token}\\}\\n`, 'gm'),
    color => `<hr class="${color.hrBorderClass} opacity-50">`
  ),
  {
    name: 'Horizontal line',
    pattern: /^(---)\n/gm,
    replacement: '<hr class="border-contrast opacity-30">',
  },

  {
    name: 'Images with title',
    pattern:
      /\!\[((\w| )+)\]\((https?\:\/{2}(\w|\.|\/|\d|-|#|&|\?|=)+) +"(([0-9a-zA-Z]| |!|\?|#|=)+)" {0,}\)/g,
    replacement: "<img src='$3' title='$5' alt='$1' height='30%'>",
  },

  {
    name: 'Images with classes',
    pattern: /\!\[([^\]]+)\]\(([^\)]+)\)\{([^\}]+)\}/g,
    replacement: "<img src='$2' alt='$1' class='object-cover $3' />",
  },
  {
    name: 'Images',
    pattern: /\!\[((\w| )+)\]\((https?\:\/{2}(\w|\.|\/|\d|-|#|&|\?|=)+)\)/g,
    replacement: "<img src='$3' alt='$1' height='30%'>",
  },
  {
    name: 'Links',
    pattern: /\[\\?((\w| )+)\]\((https?\:\/{2}(\w|\.|\/|\d|-|#|\?|=|&)+)\)/g,
    replacement:
      "<a class=\"underline cursor-pointer transition opacity-100 hover:opacity-50\" target='_blank' href='$3'>$1</a>",
  },
  ...buildColorRules(
    token =>
      new RegExp(
        `\\[\\\\?(([A-zÀ-ú]| |'|&|"|\\.|\\/|\\d|-|\\?|=)+)\\]\\(((\\w|\\.|\\/|\\d|-|#|\\?|=|&)+)\\)\\{${token}\\}`,
        'g'
      ),
    color =>
      `<a class="underline ${color.textClass} font-bold cursor-pointer transition opacity-100 hover:opacity-50" id="item-link" href='$3'>@$1</a>`,
    (token, color) => ({
      reversePattern: new RegExp(
        `<a class=Æunderline ${color.textClass} font-bold cursor-pointer transition opacity-100 hover:opacity-50Æ id=Æitem-linkÆ href='(.*)'>@(.*)<\\/a>`,
        'g'
      ),
      reverseReplacement: `[$2]($1){${token}}`,
    })
  ),
  {
    name: 'Links to item',
    pattern:
      /\[\\?(([A-zÀ-ú]| |\'|\&|\"|\.|\/|\d|-|\?|=)+)\]\(((\w|\.|\/|\d|-|#|\?|=|&)+)\)/g,
    replacement:
      '<a class="underline text-contrast font-bold cursor-pointer transition opacity-100 hover:opacity-50" id="item-link" href=\'$3\'>@$1</a>',
    reversePattern:
      /<a class=Æunderline text-contrast font-bold cursor-pointer transition opacity-100 hover:opacity-50Æ id=Æitem-linkÆ href=\'(.*)\'>@(.*)<\/a>/g,
    reverseReplacement: '[$2]($1)',
  },
  {
    name: 'Links with title',
    pattern:
      /\[((\w| )+)\]\((https?\:\/{2}(\w|\.|\/|\d|-|#|&)+) +"(([0-9a-zA-Z]| |!|\?|=)+)" {0,}\)/g,
    replacement: "<a target='_blank' href='$3'>$5</a>",
  },

  ...buildColorRules(
    token => new RegExp(`\\*{2}([^\\*]*)\\*{2}\\{${token}\\}`, 'g'),
    color => `<b class="font-weight ${color.textClass}">$1</b>`,
    (token, color) => ({
      reversePattern: new RegExp(
        `<b class=Æfont-weight ${color.textClass}Æ>([^<b]*)<\\/b>`,
        'g'
      ),
      reverseReplacement: `**$1**{${token}}`,
    })
  ),
  {
    name: 'Bold',
    pattern: /\*{2}([^\*]*)\*{2}/g,
    replacement: '<b>$1</b>',
    reversePattern: /<b>([^<b]*)<\/b>/g,
    reverseReplacement: '**$1**',
  },

  ...buildColorRules(
    token => new RegExp(`\\*{1}([^\\*]*)\\*{1}\\{${token}\\}`, 'g'),
    color => `<em class="${color.textClass}">$1</em>`,
    (token, color) => ({
      reversePattern: new RegExp(
        `<em class=Æ${color.textClass}Æ>([^<em]*)<\\/em>`,
        'g'
      ),
      reverseReplacement: `*$1*{${token}}`,
    })
  ),
  {
    name: 'Italic',
    pattern: /\*{1}([^\*]*)\*{1}/g,
    replacement: '<em>$1</em>',
    reversePattern: /<em>([^<em]*)<\/em>/g,
    reverseReplacement: '*$1*',
  },

  ...buildColorRules(
    token => new RegExp(`\\~{2}([^\\~]*)\\~{2}\\{${token}\\}`, 'g'),
    color => `<strike class="${color.textClass}">$1</strike>`,
    (token, color) => ({
      reversePattern: new RegExp(
        `<strike class=Æ${color.textClass}Æ>([^<strike]*)<\\/strike>`,
        'g'
      ),
      reverseReplacement: `~~$1~~{${token}}`,
    })
  ),
  {
    name: 'Strikethrough',
    pattern: /\~{2}([^\~]*)\~{2}/g,
    replacement: '<strike>$1</strike>',
    reversePattern: /<strike>([^<strike]*)<\/strike>/g,
    reverseReplacement: '~~$1~~',
  },

  ...buildColorRules(
    token => new RegExp(`==(.*)==\\{${token}\\}`, 'g'),
    color =>
      `<mark class="text-contrast text-opacity-75 ${color.markBgClass} px-03 py-01 rounded">$1</mark>`,
    (token, color) => ({
      reversePattern: new RegExp(
        `<mark class=Ætext-contrast text-opacity-75 ${color.markBgClass} px-03 py-01 roundedÆ>([^<mark]*)<\\/mark>`,
        'g'
      ),
      reverseReplacement: `==$1=={${token}}`,
    })
  ),
  {
    name: 'Marked',
    pattern: /==(.*)==/g,
    replacement:
      '<mark class="text-contrast text-opacity-75 bg-contrast bg-opacity-30 px-03 py-01 rounded">$1</mark>',
    reversePattern:
      /<mark class=Ætext-contrast text-opacity-75 bg-contrast bg-opacity-30 px-03 py-01 roundedÆ>([^<mark]*)<\/mark>/g,
    reverseReplacement: '==$1==',
  },

  ...buildColorRules(
    token => new RegExp(`\\+{2}([^\\+]*)\\+{2}\\{${token}\\}`, 'g'),
    color => `<span class="underline ${color.textClass}">$1</span>`,
    (token, color) => ({
      reversePattern: new RegExp(
        `<span class=Æunderline ${color.textClass}Æ>(.*)<\\/span>`,
        'g'
      ),
      reverseReplacement: `++$1++{${token}}`,
    })
  ),
  {
    name: 'Underline',
    pattern: /\+{2}([^\+]*)\+{2}/g,
    replacement: '<span class="underline">$1</span>',
    reversePattern: /<span class=ÆunderlineÆ>(.*)<\/span>/g,
    reverseReplacement: '++$1++',
  },

  ...buildColorRules(
    token => new RegExp('`([^`]*)`\\{' + token + '\\}', 'gm'),
    color =>
      `<div class='${color.codeBgClass} bg-opacity-50 rounded shadow p-1 my-1'>$1</div>`
  ),
  {
    name: 'Inline code',
    pattern: /`([\s\S]*?)`/gm,
    replacement:
      "<div class='bg-main-d bg-opacity-50 rounded shadow p-1 my-1'>$1</div>",
  },

  {
    name: 'Autoconvert link',
    pattern: /(https?:\/\/(\w|\d|\.|-|\/|#|\?|=|&)+) /g,
    replacement:
      '<a class="underline cursor-pointer transition opacity-100 hover:opacity-50" href=\'$1\'>$1</a> ',
  },
  {
    name: 'Autoconvert link with linebreak',
    pattern: /(https?:\/\/([A-zÀ-ú]|\d|\.|-|\/|#|\?|=|&)+)<br>/g,
    replacement:
      '<a class="underline cursor-pointer transition opacity-100 hover:opacity-50" href=\'$1\'>$1</a><br>',
  },

  ...buildColorRules(
    token => new RegExp(`^- (.*) \\{${token}\\}`, 'gm'),
    color =>
      `<div class="pl-1 flex gap-05 -mb-1"><div class="${color.textClass}">○</div><div>$1</div></div>`
  ),
  {
    name: 'list',
    pattern: /^- (.*)/gm,
    replacement:
      '<div class="pl-1 flex gap-05 -mb-1"><div class="text-contrast text-opacity-75">○</div><div>$1</div></div>',
  },

  ...buildColorRules(
    token => new RegExp(`^(\\d+\\.) (.*) \\{${token}\\}`, 'gm'),
    color =>
      `<div class="pl-1 flex gap-05 -mb-1"><div class="${color.textClass} text-sm mt-03">$1</div><div>$2</div></div>`
  ),
  {
    name: 'list num',
    pattern: /^(\d+\.) (.*)/gm,
    replacement:
      '<div class="pl-1 flex gap-05 -mb-1"><div class="text-contrast text-opacity-75 text-sm mt-03">$1</div><div>$2</div></div>',
  },

  {
    name: 'Line breaks',
    pattern: /\n/g,
    replacement: '<div class="pt-1"></div>',
  },
];
